// src/components/dashboard/DashboardCards.jsx
import { useState } from "react";
import { Plus } from "lucide-react";
import { Card, SectionLabel, Divider, AccountIcon, Badge, ChangeIndicator } from "../ui/primitives";
import { formatCurrency, formatDate, calcLoanPayoff } from "../../lib/utils";
import { db } from "../../lib/firebase";
import { doc, setDoc, Timestamp } from "firebase/firestore";

const USER_ID = "gavin";

// ── Accounts ──────────────────────────────────────────────────────────────
export function AccountsCard({ accounts }) {
  const accountConfig = {
    hhcu_checking: { initials: "HC", color: "blue", label: "HHCU Checking" },
    hhcu_savings: { initials: "HS", color: "teal", label: "HHCU Savings" },
    usaa_savings: { initials: "US", color: "green", label: "USAA Savings" },
    robinhood_brokerage: { initials: "RH", color: "purple", label: "Robinhood" },
  };

  return (
    <div>
      <SectionLabel>Accounts</SectionLabel>
      <Card className="divide-y divide-border p-0 overflow-hidden">
        {accounts.map((acct, i) => {
          const cfg = accountConfig[acct.id] || { initials: acct.name?.slice(0, 2).toUpperCase(), color: "blue" };
          return (
            <div key={acct.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <AccountIcon initials={cfg.initials} color={cfg.color} />
                <div>
                  <p className="text-[13px] font-medium text-text-primary">{cfg.label || acct.name}</p>
                  <p className="text-[11px] text-text-tertiary font-mono">
                    {acct.mask ? `··${acct.mask}` : acct.subtype}
                  </p>
                </div>
              </div>
              <span className="text-sm font-semibold text-text-primary font-tabular">
                {formatCurrency(acct.balance)}
              </span>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ── Credit Cards ──────────────────────────────────────────────────────────
export function CreditCardsCard({ cards }) {
  return (
    <div>
      <SectionLabel>Credit cards</SectionLabel>
      <div className="space-y-2">
        {cards.map((card) => {
          const utilization = card.limit ? (Math.abs(card.balance) / card.limit) * 100 : 0;
          return (
            <Card key={card.id} className="p-4">
              <div className="flex justify-between items-start mb-3">
                <p className="text-[13px] font-semibold text-text-primary">{card.name}</p>
                <span className="text-sm font-semibold text-accent-red font-tabular">
                  -{formatCurrency(Math.abs(card.balance))}
                </span>
              </div>
              <div className="flex gap-4">
                <div>
                  <p className="label">APR</p>
                  <p className="text-xs font-semibold text-text-primary mt-0.5">{card.apr}%</p>
                </div>
                <div>
                  <p className="label">Due</p>
                  <p className="text-xs font-semibold text-text-primary mt-0.5">
                    {card.dueDate ? formatDate(card.dueDate?.toDate?.() || card.dueDate, "MMM d") : "—"}
                  </p>
                </div>
                {card.limit && (
                  <div>
                    <p className="label">Limit</p>
                    <p className="text-xs font-semibold text-text-primary mt-0.5">
                      {formatCurrency(card.limit, { compact: true })}
                    </p>
                  </div>
                )}
              </div>
              {card.limit && (
                <div className="mt-3">
                  <div className="h-1 bg-background-tertiary rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-accent-red transition-all duration-500"
                      style={{ width: `${Math.min(utilization, 100)}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-text-tertiary mt-1 font-mono">
                    {utilization.toFixed(1)}% utilized
                  </p>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Student Loans ─────────────────────────────────────────────────────────
export function LoansCard({ loans }) {
  const totalBalance = loans.reduce((s, l) => s + Math.abs(l.balance || 0), 0);
  const totalMonthly = loans.reduce((s, l) => s + (l.monthlyPayment || 0), 0);
  const totalInterestPaid = loans.reduce((s, l) => s + (l.interestPaid || 0), 0);
  const avgRate = loans.length
    ? loans.reduce((s, l) => s + (l.interestRate || 0), 0) / loans.length
    : 0;

  // Estimate payoff using highest rate loan's schedule
  const lastPayoff = loans.reduce((latest, loan) => {
    const payoff = calcLoanPayoff(Math.abs(loan.balance), loan.interestRate, loan.monthlyPayment);
    return payoff && (!latest || payoff > latest) ? payoff : latest;
  }, null);

  return (
    <div>
      <SectionLabel>Student loans</SectionLabel>
      <Card className="p-4">
        {/* Summary header */}
        <div className="flex justify-between items-start mb-3 pb-3 border-b border-border">
          <div>
            <p className="text-xl font-bold text-accent-red font-tabular">
              -{formatCurrency(totalBalance)}
            </p>
            <p className="text-[11px] text-text-tertiary mt-0.5">
              {loans.length} loans · {formatCurrency(totalMonthly)}/mo
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-text-primary">
              {lastPayoff ? formatDate(lastPayoff, "MMM yyyy") : "—"}
            </p>
            <p className="text-[10px] text-text-tertiary">est. payoff</p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 mb-3 pb-3 border-b border-border">
          <div className="text-center">
            <p className="text-sm font-semibold text-text-primary font-tabular">
              {formatCurrency(totalInterestPaid, { compact: true })}
            </p>
            <p className="label mt-0.5">interest paid</p>
          </div>
          <div className="text-center border-x border-border">
            <p className="text-sm font-semibold text-text-primary">{avgRate.toFixed(2)}%</p>
            <p className="label mt-0.5">avg rate</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-text-primary">
              {lastPayoff ? formatDate(lastPayoff, "MMM yyyy") : "—"}
            </p>
            <p className="label mt-0.5">break-even</p>
          </div>
        </div>

        {/* Individual loans */}
        <div className="space-y-0 divide-y divide-border">
          {loans.map((loan) => (
            <div key={loan.id} className="flex justify-between items-center py-2.5">
              <div>
                <p className="text-[12px] font-medium text-text-primary">{loan.name}</p>
                <p className="text-[11px] text-text-tertiary font-mono mt-0.5">{loan.interestRate}% APR</p>
              </div>
              <span className="text-[13px] font-semibold text-accent-red font-tabular">
                -{formatCurrency(Math.abs(loan.balance))}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── Cash Flow ─────────────────────────────────────────────────────────────
export function CashFlowCard({ primaryIncome, altIncome, totalSpending }) {
  const net = primaryIncome + altIncome - totalSpending;
  const weeklyBurn = totalSpending / 4.33;

  return (
    <div>
      <SectionLabel>Cash flow · this month</SectionLabel>
      <Card className="p-4">
        <div className="space-y-2.5 mb-3">
          <div className="flex justify-between items-center">
            <span className="text-[12px] text-text-secondary">Primary income</span>
            <span className="text-[14px] font-semibold text-accent-green font-tabular">
              +{formatCurrency(primaryIncome)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[12px] text-text-secondary">Alternative income</span>
            <span className="text-[14px] font-semibold text-accent-green font-tabular">
              +{formatCurrency(altIncome)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[12px] text-text-secondary">Total spending</span>
            <span className="text-[14px] font-semibold text-accent-red font-tabular">
              -{formatCurrency(totalSpending)}
            </span>
          </div>
        </div>
        <Divider className="my-3" />
        <div className="flex justify-between items-center mb-3">
          <span className="text-[13px] font-semibold text-text-primary">Net this month</span>
          <span className={`text-lg font-bold font-tabular ${net >= 0 ? "text-accent-green" : "text-accent-red"}`}>
            {net >= 0 ? "+" : ""}{formatCurrency(net)}
          </span>
        </div>
        <div className="flex justify-between items-center pt-3 border-t border-border">
          <span className="text-[12px] text-text-tertiary">Weekly burn rate</span>
          <span className="text-[13px] font-semibold text-text-primary font-tabular">
            {formatCurrency(weeklyBurn)}/wk
          </span>
        </div>
      </Card>
    </div>
  );
}

// ── Cash on Hand ──────────────────────────────────────────────────────────
export function CashCard({ cash, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  async function handleSave() {
    const amount = parseFloat(value);
    if (isNaN(amount)) return;
    await setDoc(doc(db, "users", USER_ID, "manual", "cash"), {
      amount,
      updatedAt: Timestamp.now(),
    });
    setEditing(false);
    setValue("");
  }

  const updatedAt = cash?.updatedAt?.toDate?.() || null;

  return (
    <div>
      <SectionLabel>Cash on hand</SectionLabel>
      <Card className="p-4">
        {editing ? (
          <div className="flex gap-2 items-center">
            <span className="text-text-tertiary text-lg">$</span>
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0"
              className="flex-1 bg-background-tertiary border border-border rounded-xl px-3 py-2 text-text-primary text-lg font-semibold font-tabular focus:outline-none focus:border-accent-green transition-colors"
              autoFocus
            />
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-accent-green text-background rounded-xl text-sm font-semibold"
            >
              Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-2 border border-border rounded-xl text-sm text-text-secondary"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <div>
              <p className="text-2xl font-bold font-tabular text-text-primary">
                {formatCurrency(cash?.amount || 0)}
              </p>
              <p className="text-[11px] text-text-tertiary mt-1">
                {updatedAt
                  ? `Updated ${formatDate(updatedAt, "MMM d")} at ${updatedAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
                  : "Never updated"}
              </p>
            </div>
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-xl text-[12px] text-text-secondary hover:text-text-primary hover:border-border-strong transition-all"
            >
              <Plus size={13} />
              Update
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Top Spending Preview ───────────────────────────────────────────────────
const CATEGORY_COLORS = {
  "Eating out": "#D85A30",
  "Sports betting": "#9B7FFF",
  Groceries: "#00E5A0",
  "Gas / fuel": "#F5A623",
  Subscriptions: "#4D9FFF",
  "Bars / alcohol": "#D4537E",
  "Golf rounds": "#888780",
  Books: "#5DCAA5",
};

export function TopSpendingCard({ spending, previousSpending = {} }) {
  const top5 = spending.slice(0, 5);
  const maxAmount = top5[0]?.total || 1;

  return (
    <div>
      <SectionLabel>Top spending · this month</SectionLabel>
      <Card className="p-4 space-y-3">
        {top5.map((item) => {
          const prev = previousSpending[item.category] || item.total;
          const pctChange = prev ? ((item.total - prev) / prev) * 100 : 0;
          const color = CATEGORY_COLORS[item.category] || "#888780";
          const width = (item.total / maxAmount) * 100;

          return (
            <div key={item.category}>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-[12px] text-text-secondary">{item.category}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-text-primary font-tabular">
                    {formatCurrency(item.total)}
                  </span>
                  <ChangeIndicator value={pctChange} />
                </div>
              </div>
              <div className="h-1.5 bg-background-tertiary rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${width}%`, backgroundColor: color }}
                />
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
